import React from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Sparkle, LinkedinLogo, FacebookLogo, InstagramLogo, TiktokLogo, ArrowRight, Copy } from "@phosphor-icons/react";

const platformIcon = {
  LinkedIn: LinkedinLogo, Facebook: FacebookLogo, Instagram: InstagramLogo, TikTok: TiktokLogo,
};

function PostCard({ post }) {
  const Icon = platformIcon[post.platform] || Sparkle;
  const copyText = () => {
    const t = `${post.headline}\n\n${post.body}\n\n${(post.hashtags || []).join(" ")}`;
    navigator.clipboard.writeText(t);
    toast.success(`${post.platform} post copied`);
  };
  return (
    <div className="border border-white/[0.08] bg-[#0a0a0a] rounded-sm p-5 flex flex-col hover-lift hover:border-primary/40" data-testid={`post-card-${post.platform.toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon size={16} className="text-primary" />
          <span className="text-[10px] font-mono uppercase tracking-[0.24em] text-muted-foreground">{post.platform}</span>
        </div>
        <button onClick={copyText} data-testid={`copy-${post.platform.toLowerCase()}`} className="text-muted-foreground hover:text-primary transition-colors">
          <Copy size={14} />
        </button>
      </div>
      <div className="mt-3 font-heading font-bold text-lg tracking-tight leading-snug">{post.headline}</div>
      <div className="mt-3 text-sm text-foreground/85 whitespace-pre-wrap flex-1">{post.body}</div>
      {post.visual_direction && (
        <div className="mt-3 text-[11px] text-muted-foreground font-mono">
          <span className="text-primary uppercase tracking-widest">visual · </span>{post.visual_direction}
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-1.5">
        {(post.hashtags || []).map((h, i) => (
          <span key={i} className="text-[10px] font-mono px-2 py-0.5 border border-white/10 text-muted-foreground rounded-sm">{h}</span>
        ))}
      </div>
      {post.cta && <div className="mt-3 text-[10px] font-mono uppercase tracking-[0.24em] text-primary">CTA · {post.cta}</div>}
    </div>
  );
}

export default function AgentA() {
  const [topic, setTopic] = React.useState("");
  const [sector, setSector] = React.useState("FHA");
  const [location, setLocation] = React.useState("Memphis");
  const [tone, setTone] = React.useState("professional");
  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState(null);

  const generate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post("/agents/orchestrator/generate", { topic, loan_sector: sector, location, tone });
      setResult(r.data);
      toast.success("Multi-post rewritten across 4 networks");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Generation failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl">
      <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-muted-foreground">Agent A</div>
      <h1 className="mt-2 font-heading font-black text-4xl tracking-tighter">Multi-Post Orchestrator</h1>
      <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
        Drop one topic, loan scenario or success story. Agent A splits and reformats it for LinkedIn, Facebook,
        Instagram and TikTok — native vocabulary, localized hashtags, ready-to-post.
      </p>

      <form onSubmit={generate} className="mt-8 border border-white/[0.08] bg-[#0a0a0a] rounded-sm p-6" data-testid="agent-a-form">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="md:col-span-6">
            <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Topic / scenario</label>
            <textarea data-testid="agent-a-topic" required value={topic} onChange={(e) => setTopic(e.target.value)} rows={3} placeholder="Just closed an ITIN loan in 21 days for a family in East Memphis…" className="w-full bg-transparent border border-white/10 focus:border-primary/50 outline-none px-4 py-3 rounded-sm text-sm resize-none" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Loan sector</label>
            <select data-testid="agent-a-sector" value={sector} onChange={(e) => setSector(e.target.value)} className="w-full bg-[#0a0a0a] border border-white/10 focus:border-primary/50 outline-none px-4 py-3 rounded-sm text-sm">
              {["FHA", "ITIN", "VA", "Conventional", "DSCR", "Jumbo", "Refi"].map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Location</label>
            <input data-testid="agent-a-location" value={location} onChange={(e) => setLocation(e.target.value)} className="w-full bg-transparent border border-white/10 focus:border-primary/50 outline-none px-4 py-3 rounded-sm text-sm" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Tone</label>
            <select data-testid="agent-a-tone" value={tone} onChange={(e) => setTone(e.target.value)} className="w-full bg-[#0a0a0a] border border-white/10 focus:border-primary/50 outline-none px-4 py-3 rounded-sm text-sm">
              {["professional", "friendly", "urgent", "educational", "punchy"].map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <button type="submit" disabled={loading || topic.length < 5} data-testid="agent-a-generate" className="mt-5 inline-flex items-center gap-2 px-5 py-3 bg-primary text-primary-foreground rounded-sm font-medium primary-glow hover-lift disabled:opacity-60 text-sm">
          <Sparkle size={14} /> {loading ? "Orchestrating…" : "Generate 4 platform posts"} <ArrowRight size={14} />
        </button>
      </form>

      {loading && (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-64 border border-white/[0.06] bg-[#0a0a0a] rounded-sm animate-pulse" />
          ))}
        </div>
      )}

      {result && (
        <div className="mt-8" data-testid="agent-a-results">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-muted-foreground">Output · 4 platform rewrites</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-primary">{result.loan_sector}</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {result.posts.map((p, i) => <PostCard key={i} post={p} />)}
          </div>
        </div>
      )}
    </div>
  );
}
